/**
 * WhatsApp Link Generator Utility for Leads
 * Generates WhatsApp links with pre-written lead summary messages
 */

import {
  buildWhatsAppUrl,
  formatPhoneForWhatsApp,
  openWhatsAppUrl,
} from "../../../shared/utils/outboundMessaging.js";

export { formatPhoneForWhatsApp };

/**
 * Format items list for WhatsApp message
 * @param {Array} items - Array of lead items
 * @returns {string} Formatted items list
 */
export const formatItemsForWhatsApp = (items) => {
  if (!items || items.length === 0) {
    return "No items specified";
  }

  return items
    .map((item, index) => {
      if (typeof item === "string") {
        return `${index + 1}. ${item.trim()}`;
      }

      const content = item.content || item.description || item.caption || "";
      const quantity = item.quantity && item.quantity > 1 ? `${item.quantity}x ` : "";
      return `${index + 1}. ${quantity}${content.trim()}`;
    })
    .filter((item) => item.trim() !== "")
    .join("\n");
};

/**
 * Generate WhatsApp message for lead sharing with a service provider
 * @param {Object} lead - Lead data
 * @returns {string} Formatted WhatsApp message
 */
export const generateLeadWhatsAppMessage = (lead) => {
  if (!lead) {
    throw new Error("Lead data is required");
  }

  const leadId = lead.id || lead._id || "N/A";
  const customerName = lead.name || "N/A";
  const pickupLocation = lead.moving_from || "N/A";
  const deliveryLocation = lead.moving_to || "N/A";
  const moveDate = lead.move_date || "N/A";
  const itemsToFormat = lead.items || [];
  const inventorySummary = formatItemsForWhatsApp(itemsToFormat);
  const notes = lead.notes?.trim() ? lead.notes.trim() : "None";

  let message = `📋 *Lead Summary — ${customerName}*\n\n`;
  message += `*Lead ID:* ${leadId}\n`;
  message += `*Customer Name:* ${customerName}\n`;
  message += `*Pickup Location:* ${pickupLocation}\n`;
  message += `*Delivery Location:* ${deliveryLocation}\n`;
  message += `*Move Date:* ${moveDate}\n\n`;
  message += `*Inventory Summary:*\n${inventorySummary}\n\n`;
  message += `*Notes:*\n${notes}`;

  return message;
};

/**
 * Generate WhatsApp link with pre-written message
 * @param {Object} lead - Lead data
 * @param {string} recipientPhone - Recipient phone number (required)
 * @returns {string|null} WhatsApp link URL or null if phone number is missing/invalid
 */
export const generateLeadWhatsAppLink = (lead, recipientPhone) => {
  if (!lead) {
    throw new Error("Lead data is required");
  }
  if (!recipientPhone) {
    throw new Error("Recipient phone number is required");
  }

  const message = generateLeadWhatsAppMessage(lead);
  return buildWhatsAppUrl(recipientPhone, message);
};

/**
 * Open WhatsApp link in new tab
 * @param {Object} lead - Lead data
 * @param {string} recipientPhone - Recipient phone number (required)
 * @returns {void}
 */
export const openLeadWhatsAppLink = (lead, recipientPhone) => {
  const link = generateLeadWhatsAppLink(lead, recipientPhone);

  if (link) {
    openWhatsAppUrl(link);
  } else {
    throw new Error("Cannot generate WhatsApp link: invalid recipient phone number");
  }
};

/**
 * Format items list for WhatsApp confirmation message
 * @param {Array} items - Array of items (can be tableItems format or raw items)
 * @returns {string} Formatted items list
 */
const formatItemsListForConfirmation = (items) => {
  if (!items || items.length === 0) {
    return "No items listed";
  }

  return items
    .map((item, index) => {
      if (item.description !== undefined) {
        const description = item.description || "Item";
        const quantity = item.quantity || 1;
        const quantityText = quantity > 1 ? ` (${quantity})` : "";
        return `${index + 1}. ${description}${quantityText}`;
      }

      if (typeof item === "string") {
        return `${index + 1}. ${item.trim()}`;
      }

      const description =
        item.description || item.content || item.caption || item.filename || "Item";
      const quantity = item.quantity || 1;
      const quantityText = quantity > 1 ? ` (${quantity})` : "";
      return `${index + 1}. ${description}${quantityText}`;
    })
    .join("\n");
};

/**
 * Generate WhatsApp message for items confirmation
 * @param {Array} items - Array of items to confirm
 * @param {Object} lead - Lead data (optional, for customer name and phone)
 * @returns {string} Formatted WhatsApp message
 */
export const generateLeadItemsConfirmationMessage = (items, lead = null) => {
  if (!items || items.length === 0) {
    throw new Error("Items are required for confirmation message");
  }

  const customerName = lead?.name || lead?.contactName || "there";
  const itemsList = formatItemsListForConfirmation(items);

  let message = `Good day ${customerName}! 👋\n\n`;
  message += `Please confirm the following items list for your move:\n\n`;
  message += `${itemsList}\n\n`;
  message += `Please review the list above and confirm if everything is correct, or let me know if there are any changes needed.\n\n`;
  message += `Thank you! 🙏`;

  return message;
};

/**
 * Generate WhatsApp link for items confirmation
 * @param {Array} items - Array of items to confirm
 * @param {Object} lead - Lead data
 * @returns {string|null} WhatsApp link URL or null if phone number is missing
 */
export const generateLeadItemsConfirmationLink = (items, lead) => {
  if (!items || items.length === 0) {
    throw new Error("Items are required");
  }

  if (!lead) {
    throw new Error("Lead data is required");
  }

  const phone = lead.phone;
  if (!phone) {
    return null;
  }

  const message = generateLeadItemsConfirmationMessage(items, lead);
  return buildWhatsAppUrl(phone, message);
};

/**
 * Open WhatsApp link for items confirmation in new tab
 * @param {Array} items - Array of items to confirm
 * @param {Object} lead - Lead data
 * @returns {void}
 */
export const openLeadItemsConfirmationLink = (items, lead) => {
  const link = generateLeadItemsConfirmationLink(items, lead);
  if (link) {
    openWhatsAppUrl(link);
  } else {
    console.warn("Cannot generate WhatsApp link: phone number missing");
  }
};
